import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import routes, { CustomRouteObject } from "./routes";
import Topbar from "./layouts/main-layout/Topbar";
import { useTheme } from "./context/ThemeContext";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useNotification } from "./context/NotificationContext";
import { useClient } from "./context/ClientContext";
import AuthService from "./services/AuthService";
import AppFooter from "./layouts/shared-layout/AppFooter";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

interface Module {
  name: string;
  icon: string;
  bgIcon: IconProp;
  permissions: string[];
  route: string;
}

const containerVariants = {
  hidden: { opacity: 1 },
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, x: 100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const LandingPage: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [animationKey, setAnimationKey] = useState<number>(0); // Key to re-trigger animations
  const navigate = useNavigate();
  const { theme } = useTheme();
  const notification = useNotification();
  const client = useClient("AUTH_SERVICE");
  const authService = new AuthService(client, notification);

  const extractModules = (
    routes: CustomRouteObject[],
    parentPath: string = ""
  ): Module[] => {
    const modules: Module[] = [];
    routes.forEach((route) => {
      if (route.path === "*") return;

      const fullPath = `${parentPath}/${route.path || ""}`.replace(/\/+/g, "/");
      if (route.children) {
        modules.push(...extractModules(route.children, fullPath));
      }
      if (route.name && route.icon) {
        modules.push({
          name: route.name,
          icon: route.icon,
          bgIcon: route.bgIcon,
          permissions: route.permissions || [],
          route: fullPath,
        } as Module);
      }
    });
    return modules;
  };

  useEffect(() => {
    const extractedModules = extractModules(routes);
    const filteredModules = extractedModules.filter(
      (module) =>
        module.permissions.length === 0 ||
        authService.hasPermission(module.permissions)
    );

    setModules(filteredModules);

    // Reset animation key to re-trigger animations
    setAnimationKey((prevKey) => prevKey + 1);
  }, []);

  const tabClick = (module: Module) => {
    if (module.route) {
      navigate(module.route);
    } else {
      alert("Route Not Defined");
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <Topbar />
      <div
        className={`flex flex-col items-center justify-center flex-grow p-8 ${
          theme === "night" ? "bg-gray-400" : "bg-primary-100"
        }`}
      >
        <motion.div
          key={animationKey} // Reset animation key when ukirudi kwenye page
          className="grid grid-cols-12 gap-6 w-full max-w-6xl"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {modules.length > 0 ? (
            modules.map((module, i) => (
              <motion.div
                key={i}
                variants={cardVariants}
                onClick={() => tabClick(module)}
                className="
                          col-span-12
                          xl:col-span-4
                          lg:col-span-6
                          md:col-span-6
                          sm:col-span-12
                          h-full cursor-pointer"
              >
                <div
                  className={`group relative h-56 w-full rounded-3xl ${
                    theme === "night"
                      ? "bg-gray-300 text-white hover:bg-gray-500"
                      : "bg-primary-300 hover:bg-primary-500"
                  } p-6 shadow-lg transition-transform duration-300 border-4 border-accent-50`}
                >
                  <div
                    className={`absolute -left-4 -top-4 flex items-center justify-center w-16 h-16 rounded-full ${
                      theme === "night" ? "bg-gray-400" : "bg-primary-100"
                    }  duration-500 group-hover:translate-x-3`}
                  >
                    <div
                      className={`absolute flex items-center justify-center w-14 h-14 rounded-full ${
                        theme === "night" ? "bg-gray-500" : "bg-primary-700"
                      } ${
                        theme === "night"
                          ? "group-hover:bg-gray-300"
                          : "group-hover:bg-primary-300"
                      } duration-500 `}
                    >
                      <i
                        className={`material-icons ${
                          theme === "night"
                            ? "text-primary-900 group-hover:text-primary-700"
                            : "text-primary-400 group-hover:text-primary-500"
                        }`}
                        style={{ fontSize: "40px" }}
                      >
                        {module.icon}
                      </i>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3
                      className={`text-lg font-bold text-primary-900 group-hover:text-primary-700`}
                    >
                      {module.name}
                    </h3>
                    <div className="absolute right-0 opacity-15 ">
                      <FontAwesomeIcon
                        icon={module.bgIcon}
                        className="duration-500 scale-75 group-hover:scale-100 text-6xl"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              className="col-span-12 flex justify-center items-center"
              variants={cardVariants}
            >
              <div className="flex flex-col items-center p-6 bg-white text-center rounded-lg shadow-md border">
                {/* Icon at the top */}
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-gray-400 text-6xl mb-4"
                />

                {/* Main heading */}
                <h2 className="text-2xl font-bold text-gray-700 mb-2">
                  No Access to Modules
                </h2>

                {/* Supporting text */}
                <p className="text-gray-500 mb-4 max-w-sm">
                  It looks like you don’t currently have permissions to any
                  module. If you think this is a mistake or need access, please
                  contact your administrator.
                </p>

                {/* Optional CTA button */}
                <button
                  onClick={() => /* Possibly link to a help page or show a dialog */ {}}
                  className="px-4 py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors"
                >
                  Request Access
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
      {/* Footer */}
      <AppFooter />
      {/* You can also pass props or force modal, e.g.:
          <AppFooter forceModal productionYear={2024} orgName="Your Org" />
      */}
    </div>
  );
};

export default LandingPage;
